import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * Every submenu destination in the mega menu, including the group headings, which are themselves
 * links. Deliberately not `:not(.mega-menu__link--parent)`: a shopper pointing at "Outerwear"
 * expects a preview as much as one pointing at "Jackets".
 */
const PREVIEW_LINK_SELECTOR = '.mega-menu__link';

/**
 * Session cache of decoded preview images, keyed by resolved `src`.
 *
 * Module scope rather than instance scope, so a Theme Editor section reload — which replaces the
 * custom element — does not discard work the browser has already done. The value is the promise,
 * not the image, so two rapid hovers over the same link share one in-flight decode instead of
 * racing. Entries are never evicted: the set is bounded by the number of menu destinations, and the
 * images themselves live in the HTTP cache, not here.
 *
 * @type {Map<string, Promise<void>>}
 */
const previewDecodeCache = new Map();

/**
 * Load and decode a preview image before it is shown, so the swap is a single paint with no
 * intermediate blank or half-decoded frame.
 *
 * @param {{src: string, srcset?: string}} entry
 * @param {string} sizes - The `sizes` attribute of the element that will display it, so the
 *   preloader resolves the same srcset candidate the element will and the swap is a cache hit.
 * @returns {Promise<void>}
 */
function preloadPreviewImage(entry, sizes) {
  const cached = previewDecodeCache.get(entry.src);

  if (cached) return cached;

  const promise = new Promise((resolve, reject) => {
    const image = new Image();

    image.onerror = () => reject(new Error(`Preview image failed to load: ${entry.src}`));

    // `sizes` must be set before `srcset` for the selection to use it.
    if (entry.srcset) {
      if (sizes) image.sizes = sizes;
      image.srcset = entry.srcset;
    }

    image.src = entry.src;

    if (typeof image.decode === 'function') {
      image.decode().then(resolve, reject);
    } else {
      image.onload = () => resolve();
    }
  });

  // A failure must not be cached as a permanent verdict, and an unhandled rejection must not reach
  // the console for a decorative image.
  promise.catch(() => previewDecodeCache.delete(entry.src));

  previewDecodeCache.set(entry.src, promise);

  return promise;
}

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), { signal });

    // BONQUERÉ (Sprint 3.4.15, BNQ-WEB-0492) — Escape and outside-click dismissal.
    // Both are registered on the abort signal so `disconnectedCallback` tears them down; without
    // that, every Theme Editor section reload would leave another live pair behind.
    document.addEventListener('keydown', this.#onKeyDown, { signal });
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, { signal });

    // BONQUERÉ (Sprint 3.4.15, BNQ-WEB-0490) — overlay coordination.
    // Search, cart, account and quick-add are native <dialog>s that open into the top layer, so
    // they are never occluded by the menu — but the menu would otherwise stay open behind their
    // backdrop until the pointer happened to leave the header. One listener on the shared
    // `dialog:open` event covers all four rather than four bespoke hooks.
    //
    // `capture: true` is REQUIRED, not incidental: `DialogOpenEvent` is a plain CustomEvent with
    // `bubbles: false`, so it never reaches document during the bubble phase — which is why
    // `cart-drawer.js` listens on the element itself. Capturing listeners on an ancestor still
    // receive non-bubbling events, because `bubbles: false` suppresses only the bubble phase.
    document.addEventListener(DialogOpenEvent.eventName, this.#onOverlayOpen, { signal, capture: true });

    // BONQUERÉ (Sprint 3.4.15, BNQ-WEB-0511) — dynamic submenu media preview.
    // Delegated from the host element rather than bound per link: the panels are rendered by Liquid
    // and by the Section Rendering API, so there is no single moment at which every link exists.
    // `pointerover` and `focusin` both bubble, so one listener each covers all three panels and
    // guarantees the keyboard produces exactly the same result as the pointer — the handler cannot
    // tell them apart, because it does not look at the event type.
    this.addEventListener('pointerover', this.#onPreviewCue, { signal });
    this.addEventListener('focusin', this.#onPreviewCue, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * The item Escape just closed, held only until the resulting focus event has been seen.
   *
   * Escape must return focus to the trigger, but focusing the trigger fires the declarative
   * `on:focus="/activate"` binding, which reopened the panel the instant it closed. Rather than
   * race that with a timer, the offending item is recorded and the single next focus activation
   * for it is ignored. Pointer re-entry is unaffected, so hovering away and back still opens.
   *
   * @type {HTMLElement | null}
   */
  #escapedItem = null;

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    // Swallow exactly one focus-driven activation after Escape, so returning focus to the trigger
    // does not immediately reopen the panel the user just dismissed.
    if (this.#escapedItem) {
      const wasEscaped = this.#escapedItem === item;
      const isFocusEvent = event.type === 'focus' || event.type === 'focusin';

      this.#escapedItem = null;

      if (wasEscaped && isFocusEvent) return;
    }

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';

      // Switching straight from one menu to another never runs `#deactivate`, so the outgoing
      // panel kept `data-active` and stayed rendered. That matters now that `data-active` gates
      // `content-visibility`: without this, hovering all three menus leaves all three panels
      // rendered and their promo images resident for the rest of the page's life.
      const previousSubmenu = findSubmenu(previouslyActiveItem);

      if (previousSubmenu) delete previousSubmenu.dataset.active;

      // Switching menus leaves the outgoing panel showing whichever submenu was last pointed at.
      // Reset it now, or reopening it later starts on a stale preview.
      this.#resetPreview(previousSubmenu);
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');

    this.#warmFirstPreview(submenu);
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    // BONQUERÉ (Sprint 3.4.15, BNQ-WEB-0493) — do not close when focus or the pointer is merely
    // moving deeper into the same menu item, i.e. from the trigger into the panel's own links and
    // promo cards. Without this guard, tabbing off the trigger schedules a close and the panel
    // becomes unreachable by keyboard. `relatedTarget` is the element gaining focus on focusout,
    // and the element being entered on pointerleave, so one check covers both.
    const { relatedTarget } = event;

    if (relatedTarget instanceof Node) {
      const listItem = event.target.closest('.menu-list__list-item');

      if (listItem?.contains(relatedTarget)) return;
    }

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    // Restore before the panel is un-rendered, so the next open starts on the top-level media.
    this.#resetPreview(submenu);

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Close the active menu immediately, bypassing the deactivation debounce.
   *
   * `#deactivate` is the polite path: it is debounced, and it declines to close while the overflow
   * menu is hovered. Escape, an outside click and another header overlay opening are all explicit
   * dismissals, so they must close regardless of where the pointer happens to be resting.
   *
   * @param {boolean} [returnFocus] - Whether to move focus back to the trigger.
   */
  #closeImmediately(returnFocus = false) {
    const item = this.#state.activeItem;

    if (!item) return;

    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) delete submenu.dataset.active;

    this.#resetPreview(submenu);

    if (returnFocus) {
      this.#escapedItem = item;
      item.focus();
    }
  }

  /**
   * Close on Escape and hand focus back to the trigger, so keyboard users are never stranded
   * inside a panel they cannot dismiss.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    // Only reclaim focus if it is currently somewhere inside this menu. Escape pressed elsewhere
    // on the page should still close the menu, but must not steal focus.
    const focusIsInside = this.contains(document.activeElement);

    this.#closeImmediately(focusIsInside);
  };

  /**
   * Close when a pointer goes down anywhere outside the menu.
   * @param {PointerEvent} event
   */
  #onDocumentPointerDown = (event) => {
    if (!this.#state.activeItem) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.#closeImmediately();
  };

  /**
   * Close when any other header overlay (search, cart, account, quick-add) opens.
   */
  #onOverlayOpen = () => {
    this.#closeImmediately();
  };

  /* ------------------------------------------------------------------ *
   * Dynamic submenu media preview (BNQ-WEB-0511)
   * ------------------------------------------------------------------ */

  /**
   * Per-panel preview state, resolved lazily on first use and keyed by the panel element so it is
   * collected with the panel when the Theme Editor replaces the section.
   *
   * @typedef {object} PreviewState
   * @property {Record<string, {src: string, srcset: string, w: number, h: number, focal: string, alt: string, label: string}>} items
   * @property {HTMLElement} card - The preview frame: the rail's first card.
   * @property {HTMLImageElement} image
   * @property {HTMLElement | null} label - Eyebrow span.
   * @property {HTMLElement | null} heading
   * @property {string} menuTitle - The top-level menu's own title, used as the eyebrow.
   * @property {object | null} idle - The card as rendered, captured before the first swap.
   * @property {string | null} current - The href currently previewed, or null when idle.
   * @property {number} token - Monotonic counter that invalidates in-flight swaps.
   *
   * @type {WeakMap<Element, PreviewState | null>}
   */
  #previewStates = new WeakMap();

  /**
   * Resolve — and memoise — the preview state for a panel. A null result means this panel has no
   * preview frame, which is the normal case for a menu with no promo block configured.
   *
   * @param {Element | null} submenu
   * @returns {PreviewState | null}
   */
  #previewStateFor(submenu) {
    if (!submenu) return null;

    const memoised = this.#previewStates.get(submenu);

    if (memoised !== undefined) return memoised;

    /** @type {PreviewState | null} */
    let state = null;

    const payload = submenu.querySelector('[data-bnq-preview-map]');
    const card = submenu.querySelector('[data-bnq-preview-card]');
    const image = card?.querySelector('[data-bnq-preview-media] img');

    if (payload && card instanceof HTMLElement && image instanceof HTMLImageElement) {
      let data = null;

      try {
        data = JSON.parse(payload.textContent || '');
      } catch {
        // A malformed payload must degrade to the static card, not break the menu.
        data = null;
      }

      if (data?.items) {
        state = {
          items: data.items,
          card,
          image,
          label: card.querySelector('[data-bnq-preview-label]'),
          heading: card.querySelector('[data-bnq-preview-heading]'),
          menuTitle:
            submenu
              .closest('.menu-list__list-item')
              ?.querySelector('.menu-list__link-title')
              ?.textContent?.trim() ?? '',
          idle: null,
          current: null,
          token: 0,
        };

        // Two override blocks bound to one destination is a merchant mistake whose only symptom is
        // the wrong picture. Resolution is deterministic — first block wins — but silence is not
        // helpful, so it is surfaced where the merchant is actually working.
        if (window.Shopify?.designMode && data.duplicates?.length) {
          console.warn(
            '[BONQUERÉ] Duplicate mega-menu submenu preview overrides; the first block wins for:',
            data.duplicates.join(', ')
          );
        }
      }
    }

    this.#previewStates.set(submenu, state);

    return state;
  }

  /**
   * Pointer or keyboard cue anywhere inside the menu.
   *
   * Anything that is not a submenu link — panel padding, the promo rail itself, the top-level
   * trigger — resolves to the idle state, which is what makes "no submenu active shows the
   * top-level promotional media" fall out of a single rule rather than a set of exit handlers.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  #onPreviewCue = (event) => {
    const { target } = event;

    if (!(target instanceof Element)) return;

    const submenu = target.closest('.menu-list__submenu');

    if (!submenu) return;

    const state = this.#previewStateFor(submenu);

    if (!state) return;

    this.#setPreview(state, target.closest(PREVIEW_LINK_SELECTOR));
  };

  /**
   * @param {PreviewState} state
   * @param {Element | null} link - The submenu link being pointed at, or null for the idle state.
   */
  #setPreview(state, link) {
    // The rail is `display: none` below 990px. Bailing on the rendered box rather than on a media
    // query means the preview can never fetch an image for a frame nobody can see, whatever the
    // breakpoint is later changed to.
    if (state.card.offsetParent === null) return;

    const href = link?.getAttribute('href') ?? null;
    const entry = href ? state.items[href] : null;
    const next = entry ? href : null;

    // `pointerover` fires again for every descendant span of the same link.
    if (state.current === next) return;

    state.current = next;

    const token = ++state.token;

    if (!entry || !link) {
      this.#restoreIdle(state);
      return;
    }

    this.#captureIdle(state);

    preloadPreviewImage(entry, state.image.getAttribute('sizes') ?? '')
      .then(() => {
        if (state.token !== token) return;
        this.#paintPreview(state, entry, link, true);
      })
      .catch(() => {
        if (state.token !== token) return;
        // Fallback 4: the frame's own neutral tint, with the text still correct.
        this.#paintPreview(state, entry, link, false);
      });
  }

  /**
   * Record the card exactly as Liquid rendered it, once, so the idle state is whatever is genuinely
   * on screen rather than a second copy of the settings that could drift from it.
   *
   * @param {PreviewState} state
   */
  #captureIdle(state) {
    if (state.idle) return;

    const { image, card, label, heading } = state;

    state.idle = {
      src: image.getAttribute('src'),
      srcset: image.getAttribute('srcset'),
      width: image.getAttribute('width'),
      height: image.getAttribute('height'),
      objectPosition: image.style.objectPosition,
      hidden: image.hidden,
      label: label?.textContent ?? '',
      heading: heading?.textContent ?? '',
      href: card.getAttribute('href'),
      ariaLabel: card.getAttribute('aria-label'),
    };
  }

  /**
   * @param {PreviewState} state
   * @param {{src: string, srcset: string, w: number, h: number, focal: string, alt: string, label: string}} entry
   * @param {Element} link
   * @param {boolean} imageReady - False when the image could not be decoded.
   */
  #paintPreview(state, entry, link, imageReady) {
    const { image, card, label, heading } = state;
    const linkTitle = link.querySelector('.mega-menu__link-title')?.textContent?.trim() ?? '';
    const previewHeading = entry.label || linkTitle;

    image.dataset.bnqSwapping = '';

    if (imageReady) {
      image.hidden = false;
      if (entry.srcset) image.setAttribute('srcset', entry.srcset);
      image.setAttribute('src', entry.src);
      if (entry.w) image.setAttribute('width', String(entry.w));
      if (entry.h) image.setAttribute('height', String(entry.h));
      image.style.objectPosition = entry.focal || 'center center';
    } else {
      image.hidden = true;
    }

    if (label) label.textContent = state.menuTitle;
    if (heading) heading.textContent = previewHeading;

    // A card whose picture and caption describe one destination must not link to another. A card
    // rendered as a `<figure>` — no destination configured — stays non-interactive.
    if (card instanceof HTMLAnchorElement) {
      const href = link.getAttribute('href');

      if (href) card.setAttribute('href', href);

      const composed = state.menuTitle && previewHeading ? `${state.menuTitle} — ${previewHeading}` : previewHeading;
      const accessibleName = entry.alt || composed;

      if (accessibleName) card.setAttribute('aria-label', accessibleName);
    }

    this.#releaseSwap(state, state.token);
  }

  /**
   * Return the card to the state Liquid rendered. No fade: the idle image is already decoded, so
   * anything other than an immediate restore reads as a flicker when the pointer crosses a gap
   * between two links.
   *
   * @param {PreviewState} state
   */
  #restoreIdle(state) {
    const idle = state.idle;

    if (!idle) return;

    const { image, card, label, heading } = state;

    image.hidden = idle.hidden;

    if (idle.srcset) image.setAttribute('srcset', idle.srcset);
    else image.removeAttribute('srcset');

    if (idle.src) image.setAttribute('src', idle.src);
    if (idle.width) image.setAttribute('width', idle.width);
    if (idle.height) image.setAttribute('height', idle.height);

    image.style.objectPosition = idle.objectPosition;
    delete image.dataset.bnqSwapping;

    if (label) label.textContent = idle.label;
    if (heading) heading.textContent = idle.heading;

    if (card instanceof HTMLAnchorElement) {
      if (idle.href) card.setAttribute('href', idle.href);

      if (idle.ariaLabel) card.setAttribute('aria-label', idle.ariaLabel);
      else card.removeAttribute('aria-label');
    }
  }

  /**
   * Release the one blanked frame that separates the outgoing image from the incoming one.
   *
   * @param {PreviewState} state
   * @param {number} token
   */
  #releaseSwap(state, token) {
    requestAnimationFrame(() => {
      if (state.token !== token) return;
      delete state.image.dataset.bnqSwapping;
    });
  }

  /**
   * Reset a panel to its idle media, used whenever the panel stops being the active one.
   *
   * @param {Element | null} submenu
   */
  #resetPreview(submenu) {
    const state = submenu ? this.#previewStates.get(submenu) : null;

    if (!state) return;

    state.token++;
    state.current = null;
    this.#restoreIdle(state);
  }

  /**
   * Warm the first preview of a freshly opened panel, so the first hover is instant rather than
   * waiting on a round trip. Exactly one image, on idle time, and only for a panel the shopper has
   * already opened — never on page load, and never the whole set.
   *
   * @param {Element | null} submenu
   */
  #warmFirstPreview(submenu) {
    const state = this.#previewStateFor(submenu);

    if (!state) return;
    if (navigator.connection?.saveData) return;

    const [first] = Object.values(state.items);

    if (!first || previewDecodeCache.has(first.src)) return;

    const warm = () => {
      if (state.card.offsetParent === null) return;
      preloadPreviewImage(first, state.image.getAttribute('sizes') ?? '').catch(() => {});
    };

    if (typeof requestIdleCallback === 'function') requestIdleCallback(warm, { timeout: 1000 });
    else setTimeout(warm, 200);
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
